/* @flow */

import type { Match } from "react-router-dom";

import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import cn from "classnames";
import { useTranslate } from "@awardit/react-use-translate";
import { StoreInfoContext } from "entrypoint/shared";
import Wrapper from "components/Wrapper";
import SearchMenu from "../SearchMenu";
import Logo from "components/AppHeader/Logo";
import HamburgerMenu from "components/AppHeader/HamburgerMenu";
import Button from "components/Button";
import Hamburger from "components/Hamburger";
import { MODE } from "state/view-mode";
import { useUi } from "helpers/ui";
import HomeIcon from "icons/home.svg";
import SearchIcon from "icons/search.svg";
import ChevronIcon from "icons/chevron.svg";
import ArrowIcon from "icons/arrow.svg";
import CloseIcon from "icons/close.svg";
import useHeaderScroll from "helpers/use-header-scroll";
import { CollapsableHorizontalNav, NotificationBar, useModal } from "@crossroads/ui-components";

import styles from "./styles.scss";

type HiddenCategoriesProps = {
  open: boolean,
  setOpen: boolean => void,
  categories: $ReadOnlyArray<{ +url: string, +name: string }>,
};

type NavProps = {
  showMiniBanner: boolean,
  miniBannerContent: string,
};

const headerHeight = Number.parseInt(styles.headerHeight, 10);

const navLinkIsActive = (match: Match, viewMode: string) => {
  if (!match) {
    return false;
  }

  if (viewMode !== MODE.NORMAL) {
    return false;
  }

  return true;
};

const HiddenCategories = ({ open, setOpen, categories }: HiddenCategoriesProps) => {
  const t = useTranslate();
  const { Modal } = useModal(open, setOpen);
  const { viewMode } = useUi();

  return (
    <Modal>
      <div id="hiddenCategories" className={styles.hiddenCategories}>
        <Wrapper>
          <ul>
            {categories.map(x => (
              <li key={x.url}>
                <NavLink
                  to={x.url}
                  activeClassName={styles.active}
                  isActive={match => navLinkIsActive(match, viewMode)}
                  onClick={() => setOpen(false)}
                >
                  <ArrowIcon aria-label={t("ICONS.CHEVRON")} />
                  <span>{x.name}</span>
                </NavLink>
              </li>
            ))}
          </ul>
          <Button
            type="button"
            aria-label={t("HEADER.CLOSE")}
            className={styles.closeDialogue}
            onClick={() => setOpen(false)}
          >
            <CloseIcon aria-label={t("ICONS.CROSS")} />
          </Button>
        </Wrapper>
      </div>
    </Modal>
  );
};

const NavLinks = () => {
  const t = useTranslate();
  const { viewMode } = useUi();
  const categoriesRef = useRef();
  const toggleRef = useRef();

  const { categories } = useContext(StoreInfoContext);
  const [hiddenItems, setHiddenItems] = useState([]);
  const [moreCategoriesOpen, setMoreCategoriesOpen] = useState(false);

  return (
    <div ref={categoriesRef} className={styles.links}>
      <NavLink
        to="/"
        className={cn(styles.item, styles.item__home)}
      >
        <HomeIcon aria-label={t("ICONS.HOME")} />
      </NavLink>

      <CollapsableHorizontalNav
        className={styles.collapsableNav}
        items={categories}
        setHiddenItems={setHiddenItems}
        renderItem={item => (
          <div key={item.url}>
            <NavLink
              to={item.url}
              activeClassName={styles.active}
              className={styles.item}
              isActive={match => navLinkIsActive(match, viewMode)}
            >
              <span>{item.name}</span>
            </NavLink>
          </div>
        )}
      >
        <div
          ref={toggleRef}
          className={cn(styles.item, styles.item__toggle, { [styles.open]: moreCategoriesOpen })}
        >
          <Button
            type="button"
            aria-expanded={moreCategoriesOpen ?? "true"}
            aria-controls="hiddenCategories"
            onClick={() => setMoreCategoriesOpen(!moreCategoriesOpen)}
          >
            {t("HEADER.CATEGORIES_TOGGLE")}
            <ChevronIcon aria-label={t("ICONS.CHEVRON")} />
          </Button>
        </div>
      </CollapsableHorizontalNav>

      <HiddenCategories
        categories={hiddenItems}
        open={moreCategoriesOpen}
        setOpen={setMoreCategoriesOpen}
      />
    </div>
  );
};

const Nav = ({ showMiniBanner, miniBannerContent }: NavProps): React$Node => {
  const t = useTranslate();
  const location = useLocation();
  const {
    setHamburgerOpen,
    toggleHamburger,
    setSubNavOpen,
    viewMode,
    setViewMode,
    hamburgerOpen,
  } = useUi();

  const headerScroll = useHeaderScroll(headerHeight);
  const onCheckout = location.pathname.includes("/checkout") && location.pathname !== "/checkout/success";

  const toggleSearch = () =>
    setViewMode(viewMode === MODE.SEARCH ? MODE.NORMAL : MODE.SEARCH);

  // Close menus when switching page
  useEffect(() => {
    setViewMode(MODE.NORMAL);
    setSubNavOpen(false);
    setHamburgerOpen(false);
  }, [setViewMode, setSubNavOpen, setHamburgerOpen, location.pathname]);

  return (
    <div className={cn(
      styles[`mode__${viewMode}`],
      { [styles.hamburgerOpen]: hamburgerOpen }
    )}
    >
      <div className={cn(
        styles.block,
        { [styles.hidden]: headerScroll.isHidden }
      )}
      >
        <Wrapper className={styles.wrapper}>
          <div className={styles.wrapperInner}>
            <nav aria-label={t("HEADER.MAIN_NAVIGATION")} className={styles.menu}>
              <div className={styles.left}>
                {onCheckout && (
                  <NavLink
                    to="/"
                    className={cn(
                      styles.item,
                      styles.item__home,
                      { [styles.visible]: onCheckout }
                    )}
                  >
                    <HomeIcon aria-label={t("ICONS.HOME")} />
                  </NavLink>
                )}
                {!onCheckout && (
                  <div className={styles.links}>
                    <Hamburger
                      className={cn(styles.item, styles.hamburger, styles.padding)}
                      onClick={toggleHamburger}
                    />
                    <NavLinks />
                  </div>
                )}
              </div>
              <div className={styles.right}>
                <div className={styles.nav}>
                  {!onCheckout && (
                    <>
                      <NavLink
                        to={t("CUSTOMER_SERVICE.LINK")}
                        activeClassName={styles.active}
                        className={cn(styles.item, styles.item__customerService)}
                        isActive={match => navLinkIsActive(match, viewMode)}
                      >
                        <span>{t("CUSTOMER_SERVICE.ALT_TEXT")}</span>
                      </NavLink>

                      <Button
                        type="button"
                        aria-label={viewMode === MODE.SEARCH ? t("SEARCH.OPEN") : t("SEARCH.CLOSE")}
                        aria-expanded={viewMode === MODE.SEARCH ?? "true"}
                        aria-controls="searchForm"
                        className={cn(
                          styles.item,
                          styles.item__search,
                          { [styles.active]: viewMode === MODE.SEARCH }
                        )}
                        onClick={toggleSearch}
                      >
                        <SearchIcon aria-label={t("ICONS.SEARCH")} />
                      </Button>
                      {viewMode === MODE.SEARCH &&
                        <div role="search" aria-label={t("HEADER.SEARCH")} className={cn(styles.desktop, styles.subNav, styles.search)}>
                          <SearchMenu onClose={() => toggleSearch()} />
                        </div>
                      }
                    </>
                  )}

                  <Link
                    className={cn(
                      styles.item__logo,
                      { [styles.hidden]: hamburgerOpen }
                    )}
                    to="/"
                  >
                    <Logo
                      className={styles.logo}
                      onClick={() => setViewMode(MODE.NORMAL)}
                    />
                  </Link>
                </div>
              </div>
            </nav>
          </div>
          {showMiniBanner &&
            <NotificationBar
              closeBtnTitle={t("NOTIFICATIONBAR.CLOSE")}
              className={styles.miniBanner}
              text={miniBannerContent}
              onClose={() => {}}
            />
          }
        </Wrapper>

        <HamburgerMenu />
      </div>
    </div>
  );
};

export default Nav;
